import type { ReactNode } from "react";
import type { AiPlantCare, AiPlantEncyclopedia, AiPlantProfile, GrowingMedium } from "../../shared/types";
import { GROWING_MEDIA } from "../../shared/types";
import { growingMediumFilterLabel } from "../plantMetadata";

const GROWING_OPTIONS: { value: GrowingMedium; label: string }[] = GROWING_MEDIA.map((value) => ({
  value,
  label: growingMediumFilterLabel(value),
}));

function InfoBubble(props: { label: string; children: ReactNode; variant?: "care" | "fact" | "trivia" }) {
  const variant = props.variant ?? "care";
  return (
    <div className={`plant-bubble plant-bubble--${variant}`}>
      <span className="plant-bubble__label">{props.label}</span>
      <p className="plant-bubble__body">{props.children}</p>
    </div>
  );
}

function CareBubbles({ care }: { care: AiPlantCare }) {
  const items: { label: string; value: string }[] = [
    { label: "Water", value: care.water },
    { label: "Light", value: care.light },
    { label: "Humidity", value: care.humidity },
    { label: "Soil", value: care.soil },
    { label: "Fertilizer", value: care.fertilizer },
    { label: "Common issues", value: care.common_issues },
  ];

  return (
    <section className="plant-info-section">
      <h3 className="plant-info-section__title">Common care needs</h3>
      <div className="plant-bubbles plant-bubbles--care">
        {items.map((item) => (
          <InfoBubble key={item.label} label={item.label} variant="care">
            {item.value}
          </InfoBubble>
        ))}
      </div>
    </section>
  );
}

function EncyclopediaTree({ enc }: { enc: AiPlantEncyclopedia }) {
  const branches: { label: string; value: string }[] = [
    { label: "Name / Latin", value: enc.latin_etymology },
    { label: "Origin & habitat", value: enc.origin_and_habitat },
    { label: "Biome", value: enc.biome_notes },
    { label: "Indoor / space", value: enc.indoor_space_benefits },
  ];

  return (
    <section className="plant-info-section">
      <h3 className="plant-info-section__title">Facts and trivia</h3>
      <p className="plant-info-section__meta muted">
        Popular-science style; not peer-reviewed. Overall confidence ~
        {Math.round(enc.overall_confidence * 100)}%.
      </p>

      <div className="plant-tree" role="tree" aria-label="Plant facts">
        <div className="plant-tree__root" role="treeitem" aria-expanded="true">
          <span className="plant-tree__root-label">Overview</span>
        </div>

        <ul className="plant-tree__branch-list">
          {branches.map((node) => (
            <li key={node.label} className="plant-tree__branch" role="treeitem">
              <div className="plant-tree__connector" aria-hidden="true" />
              <InfoBubble label={node.label} variant="fact">
                {node.value}
              </InfoBubble>
            </li>
          ))}

          {enc.jeopardy_style_factoids.length > 0 ? (
            <li className="plant-tree__branch plant-tree__branch--group" role="treeitem" aria-expanded="true">
              <div className="plant-tree__connector" aria-hidden="true" />
              <div className="plant-tree__group">
                <span className="plant-tree__group-label">Trivia</span>
                <ul className="plant-tree__leaf-list">
                  {enc.jeopardy_style_factoids.map((fact, i) => (
                    <li key={i} className="plant-tree__leaf">
                      <div className="plant-tree__leaf-connector" aria-hidden="true" />
                      <div className="plant-bubble plant-bubble--trivia">
                        <p className="plant-bubble__body">{fact}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ) : null}
        </ul>
      </div>

      {enc.sources_summary ? (
        <p className="plant-info-section__footnote muted">{enc.sources_summary}</p>
      ) : null}
    </section>
  );
}

function GrowingMediumSection(props: {
  value: GrowingMedium | null;
  saving: boolean;
  onChange: (next: GrowingMedium | null) => void;
}) {
  return (
    <section className="plant-info-section">
      <h3 className="plant-info-section__title">Growing</h3>
      <div className="plant-grow-chips" role="group" aria-label="Growing medium">
        {GROWING_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className="chip"
            disabled={props.saving}
            aria-pressed={props.value === value}
            onClick={() => props.onChange(props.value === value ? null : value)}
          >
            {label}
          </button>
        ))}
      </div>
    </section>
  );
}

export function PlantAiNotes(props: {
  profile: AiPlantProfile;
  growingMedium: GrowingMedium | null;
  growingSaving: boolean;
  onGrowingMediumChange: (next: GrowingMedium | null) => void;
}) {
  const { profile, growingMedium, growingSaving, onGrowingMediumChange } = props;
  return (
    <div className="plant-ai-notes">
      <div className="plant-identity">
        <div className="plant-bubble plant-bubble--identity">
          <span className="plant-bubble__label">Common name</span>
          <p className="plant-bubble__body plant-bubble__body--title">{profile.common_name_guess}</p>
        </div>
        {profile.scientific_name_guess ? (
          <div className="plant-bubble plant-bubble--identity plant-bubble--latin">
            <span className="plant-bubble__label">Scientific</span>
            <p className="plant-bubble__body plant-bubble__body--italic">{profile.scientific_name_guess}</p>
          </div>
        ) : null}
        {typeof profile.vision_confidence === "number" ? (
          <span className="pill plant-identity__confidence">
            ID ~{Math.round(profile.vision_confidence * 100)}%
          </span>
        ) : null}
      </div>

      {profile.encyclopedia ? <EncyclopediaTree enc={profile.encyclopedia} /> : null}
      {profile.care ? <CareBubbles care={profile.care} /> : null}
      <GrowingMediumSection
        value={growingMedium}
        saving={growingSaving}
        onChange={onGrowingMediumChange}
      />
    </div>
  );
}
